import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Globe, Award, Users, X, Mail, Phone, Briefcase, MapPin, WifiOff, RefreshCw } from 'lucide-react';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';
import type { Partner } from '../../types/database';
import toast from 'react-hot-toast';

interface PartnerFormData {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  industry: string;
  message: string;
}

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<PartnerFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    industry: '',
    message: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [activePartner, setActivePartner] = useState<string | null>(null);

  // Default partners for offline mode - Partenaires commerciaux
  const defaultPartners: Partner[] = [
    {
      id: '1',
      name: 'Heetch',
      logo: '/Heetch_Logo.png',
      website: 'https://heetch.com',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: '2',
      name: 'Yango Delivery',
      logo: '/Yango_Delivery.png',
      website: 'https://yango.com',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: '3',
      name: 'Fleetch',
      logo: '/Fleetch-Blue.png',
      website: 'https://fleetch.com',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ];

  // Partenaires institutionnels
  const institutionalPartners = [
    {
      id: 'pnud',
      name: 'PNUD',
      description: 'Programme des Nations Unies pour le Développement',
      website: 'https://www.undp.org'
    },
    {
      id: 'anpej',
      name: 'ANPEJ',
      description: 'Agence Nationale pour la Promotion de l\'Emploi des Jeunes',
      website: '#'
    },
    {
      id: 'ada',
      name: 'ADA Luxembourg',
      description: 'Appui au Développement Autonome',
      website: 'https://www.ada-microfinance.lu'
    },
    {
      id: 'pam',
      name: 'PAM',
      description: 'Programme Alimentaire Mondial',
      website: 'https://www.wfp.org'
    },
    {
      id: 'yoff',
      name: 'Commune de Yoff',
      description: 'Partenaire Local - Dakar',
      website: '#'
    }
  ];

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use safe operation to handle network errors gracefully
      const { data, error: operationError, isOffline: offline } = await safeSupabaseOperation(async () => {
        if (!supabase) throw new Error('Supabase not available');
        
        const { data, error } = await supabase
          .from('partners')
          .select('*')
          .order('created_at');

        if (error) throw error;
        return data;
      }, defaultPartners);

      setIsOffline(offline);

      if (offline) {
        // Use default partners when offline
        setPartners(defaultPartners);
        console.warn('Using default partners due to network issues');
      } else if (operationError) {
        throw operationError;
      } else {
        setPartners(data || defaultPartners);
      }

    } catch (error) {
      console.error('Error loading partners:', error);
      const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors du chargement des partenaires';
      setError(errorMessage);
      setIsOffline(true);
      setPartners(defaultPartners); // Fallback to default partners
      
      // Only show toast for unexpected errors, not network issues
      if (!errorMessage.includes('Network') && !errorMessage.includes('fetch')) {
        toast.error('Impossible de charger les partenaires. Données par défaut affichées.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success('Votre demande a été envoyée avec succès !');
      setIsFormOpen(false);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        industry: '',
        message: ''
      });
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const industries = [
    "Transport et Logistique",
    "Énergie Renouvelable",
    "Technologies",
    "Services Financiers",
    "Commerce et Distribution",
    "Autre"
  ];

  const renderPartnersContent = () => {
    if (loading) {
      return Array(3).fill(null).map((_, index) => (
        <div key={index} className="bg-white rounded-xl p-6 sm:p-8 shadow-lg animate-pulse">
          <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-200 rounded-full mx-auto mb-4 sm:mb-6"></div>
          <div className="h-5 sm:h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2 sm:mb-3"></div>
          <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      ));
    }

    if (error && !isOffline) {
      return (
        <div className="col-span-full flex flex-col items-center justify-center p-6 sm:p-8 bg-white rounded-xl shadow-lg border border-red-100">
          <WifiOff size={36} className="text-red-500 mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-2">Erreur de chargement</h3>
          <p className="text-neutral-600 text-center mb-4 text-sm sm:text-base">
            Une erreur est survenue lors du chargement des partenaires.
          </p>
          <button
            onClick={() => {
              setLoading(true);
              loadPartners();
            }}
            className="inline-flex items-center px-4 sm:px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm sm:text-base"
          >
            <RefreshCw size={16} className="mr-2" />
            Réessayer
          </button>
        </div>
      );
    }

    return partners.map((partner) => (
      <div 
        key={partner.id} 
        className={`bg-white rounded-xl p-6 sm:p-8 shadow-lg border transition-all duration-300 ${
          activePartner === partner.id 
            ? 'border-primary-500 scale-105 shadow-xl' 
            : 'border-primary-100 hover:border-primary-200 hover:shadow-xl'
        } text-center group cursor-pointer`}
        onMouseEnter={() => setActivePartner(partner.id)}
        onMouseLeave={() => setActivePartner(null)}
      >
        {partner.logo ? (
          <img 
            src={partner.logo} 
            alt={partner.name}
            className="w-16 h-16 sm:w-24 sm:h-24 object-contain mx-auto mb-4 sm:mb-6 transform transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              // Fallback to default icon if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : (
          <div className="w-16 h-16 sm:w-24 sm:h-24 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 transform transition-transform duration-500 group-hover:scale-110">
            <Building2 size={32} className="text-primary-500" />
          </div>
        )}
        {/* Fallback icon (hidden by default) */}
        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-primary-50 rounded-full items-center justify-center mx-auto mb-4 sm:mb-6 transform transition-transform duration-500 group-hover:scale-110 hidden">
          <Building2 size={32} className="text-primary-500" />
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-2 sm:mb-3">{partner.name}</h3>
        {partner.website && (
          <a 
            href={partner.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-primary-500 hover:text-primary-600 font-medium group-hover:translate-x-1 transition-transform text-sm sm:text-base"
          >
            Visiter le site
            <ArrowRight size={16} className="ml-1" />
          </a>
        )}
      </div>
    ));
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30">
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-100 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent-100 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-primary-50 px-4 py-2 rounded-full text-primary-700 text-sm font-medium mb-4">
            <Award size={16} />
            Écosystème de Partenaires
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Nos <span className="bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">Partenaires</span>
          </h2>
          <p className="text-base sm:text-lg text-neutral-600 leading-relaxed">
            Ensemble pour accélérer la transition vers une mobilité durable en Afrique
          </p>
        </div>

        {/* Partenaires Institutionnels */}
        <div className="mb-12">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">Partenaires Institutionnels</h3>
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl border border-blue-100 shadow-xl p-6 sm:p-8 md:p-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {institutionalPartners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-blue-200"
                >
                  <div className="aspect-square flex items-center justify-center mb-3">
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                      <span className="text-2xl sm:text-3xl font-bold text-blue-600">{partner.name}</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 line-clamp-2">{partner.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Stats & CTA Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="text-3xl sm:text-4xl font-bold mb-2">{partners.length}+</div>
              <div className="text-primary-100 text-sm">Partenaires Actifs</div>
            </div>
            <div className="bg-gradient-to-br from-accent-400 to-accent-500 rounded-2xl p-6 text-white shadow-lg">
              <div className="text-3xl sm:text-4xl font-bold mb-2">10+</div>
              <div className="text-accent-100 text-sm">Pays Couverts</div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 shadow-lg">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Rejoignez l'écosystème</h3>
            <p className="text-gray-600 mb-6">Devenez partenaire et participez à la révolution de la mobilité électrique en Afrique</p>
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:translate-y-[-2px] group"
            >
              Devenir Partenaire
              <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* Partner Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl sm:text-2xl font-bold text-neutral-900">Devenir Partenaire</h3>
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom de l'entreprise *
                    </label>
                    <div className="relative">
                      <Building2 size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du contact *
                    </label>
                    <div className="relative">
                      <Users size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone *
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Site web
                    </label>
                    <div className="relative">
                      <Globe size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        placeholder="https://"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secteur d'activité *
                    </label>
                    <div className="relative">
                      <Briefcase size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none text-sm"
                        required
                      >
                        <option value="">Sélectionnez un secteur</option>
                        {industries.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse *
                  </label>
                  <div className="relative">
                    <MapPin size={18} className="absolute left-3 top-3 text-gray-400" />
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      rows={2}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    rows={4}
                    placeholder="Décrivez votre projet et vos objectifs de partenariat..."
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 sm:space-x-4">
                  <button
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="px-4 sm:px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 sm:px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 text-sm"
                  >
                    {submitting ? 'Envoi en cours...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Partners;